import { createClient, RedisClientType } from "redis"

interface RedisConfig {
  url: string
}

class RedisDatabase {
  // eslint-disable-next-line no-use-before-define
  private static instance: RedisDatabase
  private client: RedisClientType | null = null
  private isConnecting: boolean = false
  private isConnected: boolean = false
  private initializationAttempted: boolean = false
  private redisUrl: string | null = null

  private constructor() {}

  public static getInstance(): RedisDatabase {
    if (!RedisDatabase.instance) {
      RedisDatabase.instance = new RedisDatabase()
    }
    return RedisDatabase.instance
  }

  public async initialize(config: RedisConfig): Promise<void> {
    if (this.initializationAttempted) {
      return
    }

    this.initializationAttempted = true
    this.redisUrl = config.url

    try {
      await this.connect()
      console.log("Redis initialized successfully at application startup")
    } catch (error) {
      console.warn(
        "Redis initialization failed (non-critical), app will continue without Redis:",
        error
      )
    }
  }

  public getClient(): RedisClientType | null {
    if (this.isConnected && this.client) {
      return this.client
    }
    return null
  }

  private async connect(): Promise<RedisClientType> {
    if (this.isConnecting || (this.isConnected && this.client)) {
      return this.client!
    }

    if (!this.redisUrl) {
      throw new Error(
        "Redis URL is required. Call initialize() with config first."
      )
    }

    this.isConnecting = true

    try {
      this.client = createClient({
        url: this.redisUrl,
      })

      this.client.on("error", (err) => {
        console.warn("Redis Client Error (non-critical):", err)
        this.isConnected = false
        // Don't throw or crash - just log and continue
      })

      this.client.on("connect", () => {
        console.log("Redis Client Connected")
        this.isConnected = true
      })

      this.client.on("disconnect", () => {
        console.log("Redis Client Disconnected")
        this.isConnected = false
      })

      await this.client.connect()
      this.isConnected = true
      this.isConnecting = false

      return this.client
    } catch (error) {
      console.warn("Failed to connect to Redis (non-critical):", error)
      this.isConnecting = false
      this.isConnected = false
      this.client = null
      throw error
    }
  }

  public async disconnect(): Promise<void> {
    try {
      if (this.client && this.isConnected) {
        await this.client.quit()
        this.isConnected = false
        this.client = null
      }
    } catch (error) {
      console.warn("Error disconnecting from Redis (non-critical):", error)
      // Force cleanup even if disconnect fails
      this.isConnected = false
      this.client = null
    }
  }

  public isClientConnected(): boolean {
    return this.isConnected && this.client !== null
  }

  public async safeExecute<T>(
    operation: (client: RedisClientType) => Promise<T>
  ): Promise<T | null> {
    try {
      const client = this.getClient()
      if (!client) {
        return null
      }
      return await operation(client)
    } catch (error) {
      console.warn("Redis operation failed (non-critical):", error)
      return null
    }
  }

  public getStatus(): {
    isConnected: boolean
    isConnecting: boolean
    initializationAttempted: boolean
  } {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      initializationAttempted: this.initializationAttempted,
    }
  }
}

export const redisBase = RedisDatabase.getInstance()
export type { RedisConfig }
